import React, { useState } from "react";
import axios from "axios";
import { base44 } from "@/api/base44Client";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { GraduationCap, FileText, Mail, ClipboardList, Clock, AlertTriangle } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import SubmitExcusalForm from "../components/student/SubmitExcusalForm";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
import { format } from "date-fns";

export default function StudentDashboard() {
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [showExcusalForm, setShowExcusalForm] = useState(false);

  const { data: user } = useQuery({
    queryKey: ['currentUser'],
    queryFn: () => base44.auth.me(),
  });

  const studentEmail = user?.email;

  const { data: settings = [] } = useQuery({
    queryKey: ['settings'],
    queryFn: () => base44.entities.Settings.list(),
  });

  const timeSettings = settings.find(s => s.setting_key === 'time_constraints') || {
    student_excusal_block_start: "08:30",
    student_excusal_block_end: "18:00"
  };

  // Convert 24-hour time to 12-hour format
  const formatTime12Hour = (time24) => {
    const [hours, minutes] = time24.split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    const hours12 = hours % 12 || 12;
    return `${hours12}:${minutes.toString().padStart(2, '0')}${period}`;
  };

  const { data: assignments = [] } = useQuery({
    queryKey: ['myAssignments', studentEmail, user?.student_id],
    queryFn: async () => {
      if (!studentEmail && !user?.student_id) return [];
      // Fetch all active roster entries once and match by email OR student_id/other_id.
      // We match multiple keys so admin uploads that don't include email still show up.
      const all = await base44.entities.RosterEntry.filter({ status: 'active' });
      const myEmail = (studentEmail || '').toLowerCase();
      const mySid = String(user?.student_id || '').trim();
      const mySidStripped = mySid.replace(/^0+/, '') || '0';
      return all.filter((e) => {
        if (myEmail && e.student_email && String(e.student_email).toLowerCase() === myEmail) return true;
        const eSid = String(e.student_id || e.other_id || '').trim();
        if (!mySid || !eSid) return false;
        return eSid === mySid || eSid.replace(/^0+/, '') === mySidStripped;
      });
    },
    enabled: !!(studentEmail || user?.student_id),
  });

  // Live ASD status for this student. The ribbon at the top of the page shows
  // ONLY when at least one roster entry with status === 'asd' AND
  // attendance_status !== 'excused' belongs to this student. A manually
  // Excused ASD record must NOT trigger the banner. When the ASD entry is
  // resolved/removed or flipped to excused, this query re-runs on window
  // focus and the ribbon disappears automatically.
  const { data: asdAssignments = [] } = useQuery({
    queryKey: ['myAsdAssignments', studentEmail, user?.student_id],
    queryFn: async () => {
      if (!studentEmail && !user?.student_id) return [];
      const all = await base44.entities.RosterEntry.filter({ status: 'asd' });
      const myEmail = (studentEmail || '').toLowerCase();
      const mySid = String(user?.student_id || '').trim();
      const mySidStripped = mySid.replace(/^0+/, '') || '0';
      return all.filter((e) => {
        // Exclude ASD rows that have been manually Excused — the banner
        // must not show for a student whose ASD is E.
        if ((e.attendance_status || '').toLowerCase() === 'excused') return false;
        if (myEmail && e.student_email && String(e.student_email).toLowerCase() === myEmail) return true;
        const eSid = String(e.student_id || e.other_id || '').trim();
        if (!mySid || !eSid) return false;
        return eSid === mySid || eSid.replace(/^0+/, '') === mySidStripped;
      });
    },
    enabled: !!(studentEmail || user?.student_id),
    staleTime: 0,
    refetchOnWindowFocus: true,
  });
  const hasASD = asdAssignments.length > 0;

  const { data: excusals = [] } = useQuery({
    queryKey: ['myExcusals', studentEmail],
    queryFn: async () => {
      if (!studentEmail) return [];
      return base44.entities.ExcusalSubmission.filter({ student_email: studentEmail });
    },
    enabled: !!studentEmail,
  });

  // Load THIS student's own record via /api/students/me — students don't have
  // permission to list all students, so the old `Student.list().find(byEmail)`
  // silently 403'd and left studentRecord undefined, freezing the courtesy
  // counter at 0 on the dashboard.
  const { data: studentRecord } = useQuery({
    queryKey: ['studentRecord', studentEmail],
    queryFn: async () => {
      const res = await axios.get(`${BACKEND_URL}/api/students/me`, {
        withCredentials: true,
      });
      return res.data;
    },
    enabled: !!user,
    // Always fetch fresh so counter updates when admin applies a courtesy excusal
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  });

  // Use student_id from Student database, or fall back to other_id from assignments
  const studentOtherId = studentRecord?.student_id || (assignments.length > 0 ? assignments[0].other_id : null);

  const { data: myConsequenceRecord } = useQuery({
    queryKey: ['myConsequenceRecord', studentOtherId],
    queryFn: async () => {
      if (!studentOtherId) return null;
      const records = await base44.entities.EAMissTracking.filter({ 
        student_id: studentOtherId,
        status: 'active'
      });
      return records.length > 0 ? records[0] : null;
    },
    enabled: !!studentOtherId,
  });

  const { data: consequenceLevels = [] } = useQuery({
    queryKey: ['consequenceLevels'],
    queryFn: () => base44.entities.ConsequenceLevel.list(),
  });

  const getConsequenceDisplay = (consequence) => {
    const level = consequenceLevels.find(l => l.level_key === consequence);
    if (level) {
      return {
        color: level.badge_color,
        label: `${level.badge_emoji} ${level.level_name}`
      };
    }

    const badges = {
      warning: { color: 'bg-green-100 text-green-800', label: '⚠️ Warning' },
      parent_contact: { color: 'bg-yellow-100 text-yellow-800', label: '📞 Parent Contact' },
      detention: { color: 'bg-orange-100 text-orange-800', label: '📋 Detention' },
      iss: { color: 'bg-red-100 text-red-800', label: '🚫 ISS' },
      admin_review: { color: 'bg-red-900 text-white', label: '⛔ Admin Review' }
    };
    return badges[consequence] || { color: 'bg-slate-100 text-slate-800', label: consequence };
  };

  const getExcusalStatus = (assignmentId) => {
    return excusals.find(e => e.roster_entry_id === assignmentId);
  };

  const isWithinBlockedTime = () => {
    const now = new Date();
    const currentTime = now.getHours() * 60 + now.getMinutes(); // minutes since midnight
    
    const [startHour, startMin] = timeSettings.student_excusal_block_start.split(':').map(Number);
    const [endHour, endMin] = timeSettings.student_excusal_block_end.split(':').map(Number);
    
    const blockStart = startHour * 60 + startMin;
    const blockEnd = endHour * 60 + endMin;
    
    return currentTime >= blockStart && currentTime < blockEnd;
  };

  const handleRequestExcusal = (assignment) => {
    if (hasASD) {
      alert("Excusal requests are not available while you are on the ASD roster. Excusals from ASD are admin corrections only.");
      return;
    }
    if (isWithinBlockedTime()) {
      alert(`Excusal requests are not available between ${formatTime12Hour(timeSettings.student_excusal_block_start)} and ${formatTime12Hour(timeSettings.student_excusal_block_end)}`);
      return;
    }
    setSelectedAssignment(assignment);
    setShowExcusalForm(true);
  };

  const totalAssignments = assignments.length;
  const pendingExcusals = excusals.filter(e => e.status === 'pending').length;
  const approvedExcusals = excusals.filter(e => e.status === 'approved').length;
  
  const courtesyUsed = studentRecord?.courtesy_excusals_used || 0;
  const courtesyLimit = studentRecord?.courtesy_excusals_limit || 3;
  const courtesyRemaining = courtesyLimit - courtesyUsed;

  const blockedTime = isWithinBlockedTime();

  if (!studentEmail) {
    return (
      <div className="p-4 md:p-8 bg-gradient-to-br from-slate-50 to-blue-50 min-h-screen">
        <div className="max-w-5xl mx-auto">
          <Alert variant="destructive">
            <Mail className="w-4 h-4" />
            <AlertDescription>
              <strong>Student Email Required:</strong> You must be logged in with your student email address to access Eagle Academy assignments.
            </AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 bg-gradient-to-br from-slate-50 to-blue-50 min-h-screen">
      <div className="max-w-7xl mx-auto space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 flex items-center gap-3">
            <GraduationCap className="w-8 h-8" />
            My Eagle Academy Assignments
          </h1>
          <p className="text-slate-600 mt-1">Welcome, {user?.full_name}</p>
          <p className="text-xs text-slate-500">Logged in as: {studentEmail}</p>
        </div>

        {/* ASD RIBBON — shows only while this student has an ASD roster entry.
            Disappears automatically when that entry is resolved/removed. */}
        {hasASD && (
          <Alert
            data-testid="student-asd-ribbon"
            className="bg-gradient-to-r from-red-800 via-red-700 to-red-800 border-red-900 shadow-xl ring-2 ring-red-900/40"
          >
            <AlertTriangle className="w-6 h-6 text-white" />
            <AlertDescription className="text-white">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
                <div>
                  <p className="text-lg font-bold tracking-wide uppercase">
                    ACTIVE ASD
                  </p>
                  <p className="text-sm text-white/90 mt-1">
                    You are currently assigned to After School Detention. Please report to the gym after school (3:45p). If you are active for ASD, you are ineligible to be excused for EA/ASD.
                    <span className="block mt-1 font-semibold">-Management</span>
                  </p>
                </div>
                <Badge className="bg-white text-red-800 font-bold text-sm px-3 py-1 shrink-0">
                  {asdAssignments.length} ASD {asdAssignments.length === 1 ? 'assignment' : 'assignments'}
                </Badge>
              </div>
            </AlertDescription>
          </Alert>
        )}

        {/* EA CONSEQUENCE ALERT BANNER */}
        {myConsequenceRecord && (
          <Alert className="bg-gradient-to-r from-red-600 to-red-700 border-red-800 shadow-lg">
            <AlertTriangle className="w-6 h-6 text-white" />
            <AlertDescription className="text-white">
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <span className="text-xl font-bold">You Have Been Flagged - Missed EA</span>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-3">
                  <div className="bg-white/20 backdrop-blur-sm rounded-lg p-3">
                    <p className="text-xs text-white/80 mb-1">Status</p>
                    <Badge className={`${getConsequenceDisplay(myConsequenceRecord.current_consequence).color} text-sm`}>
                      {getConsequenceDisplay(myConsequenceRecord.current_consequence).label}
                    </Badge>
                  </div>
                  
                  <div className="bg-white/20 backdrop-blur-sm rounded-lg p-3">
                    <p className="text-xs text-white/80 mb-1">Makeup Days</p>
                    <p className="text-lg font-bold">
                      {myConsequenceRecord.makeup_days_completed || 0} / {myConsequenceRecord.makeup_days_owed || 0} completed
                    </p>
                    <p className="text-xs text-white/90 mt-1">
                      {(myConsequenceRecord.makeup_days_owed || 0) - (myConsequenceRecord.makeup_days_completed || 0)} remaining
                    </p>
                  </div>
                  
                  <div className="bg-white/20 backdrop-blur-sm rounded-lg p-3">
                    <p className="text-xs text-white/80 mb-1">Recent Refusals</p>
                    <p className="text-sm">
                      {myConsequenceRecord.miss_history ? (
                        JSON.parse(myConsequenceRecord.miss_history)
                          .slice(-3)
                          .reverse()
                          .map(date => format(new Date(date), 'MMM d'))
                          .join(', ')
                      ) : '-'}
                    </p>
                    <p className="text-xs text-white/90 mt-1">Total: {myConsequenceRecord.miss_count || 0} refusal{myConsequenceRecord.miss_count > 1 ? 's' : ''}</p>
                  </div>
                </div>

                <div className="bg-white/20 backdrop-blur-sm rounded-lg p-3 mt-3">
                  <p className="text-sm font-semibold">
                    📋 Action Required: You must attend Eagle Academy and complete {(myConsequenceRecord.makeup_days_owed || 0) - (myConsequenceRecord.makeup_days_completed || 0)} makeup day{((myConsequenceRecord.makeup_days_owed || 0) - (myConsequenceRecord.makeup_days_completed || 0)) !== 1 ? 's' : ''}. 
                    Makeup days are automatically tracked when you attend EA.
                  </p>
                </div>
              </div>
            </AlertDescription>
          </Alert>
        )}

        {blockedTime && (
          <Alert className="bg-orange-50 border-orange-200">
            <Clock className="w-4 h-4 text-orange-700" />
            <AlertDescription className="text-orange-900">
              <strong>The 8:30AM deadline has passed.</strong> Excused Requests for today's Eagle Academy are no longer being accepted. Please plan to attend Eagle Academy.
            </AlertDescription>
          </Alert>
        )}

        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <Card className="border-none shadow-lg bg-gradient-to-br from-red-800 to-red-900 text-white">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium opacity-90">Total Assignments</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div className="text-4xl font-bold">{totalAssignments}</div>
                <ClipboardList className="w-12 h-12 opacity-80" />
              </div>
              <p className="text-sm opacity-75 mt-2">Active this cycle</p>
            </CardContent>
          </Card>

          <Card className="border-none shadow-lg bg-gradient-to-br from-blue-600 to-blue-700 text-white">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium opacity-90">Courtesy Excusals</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div className="text-4xl font-bold">{courtesyRemaining}</div>
                <GraduationCap className="w-12 h-12 opacity-80" />
              </div>
              <p className="text-sm opacity-75 mt-2">{courtesyUsed} of {courtesyLimit} used</p>
            </CardContent>
          </Card>

          <Card className="border-none shadow-lg bg-gradient-to-br from-red-700 to-red-800 text-white">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium opacity-90">Pending Excusals</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div className="text-4xl font-bold">{pendingExcusals}</div>
                <FileText className="w-12 h-12 opacity-80" />
              </div>
              <p className="text-sm opacity-75 mt-2">Awaiting review</p>
            </CardContent>
          </Card>

          <Card className="border-none shadow-lg bg-gradient-to-br from-red-600 to-red-700 text-white">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium opacity-90">Approved Excusals</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div className="text-4xl font-bold">{approvedExcusals}</div>
                <GraduationCap className="w-12 h-12 opacity-80" />
              </div>
              <p className="text-sm opacity-75 mt-2">This cycle</p>
            </CardContent>
          </Card>
        </div>

        {assignments.length === 0 ? (
          <Card className="border-none shadow-lg">
            <CardContent className="p-12 text-center">
              <GraduationCap className="w-16 h-16 mx-auto text-slate-300 mb-4" />
              <h3 className="text-xl font-semibold text-slate-700 mb-2">No Active Assignments</h3>
              <p className="text-slate-500">You currently have no Eagle Academy assignments.</p>
            </CardContent>
          </Card>
        ) : (
          <>
            <Card className="border-none shadow-lg">
              <CardHeader>
                <CardTitle>My Assignments</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Course</TableHead>
                        <TableHead>Section</TableHead>
                        <TableHead>Course Short Description</TableHead>
                        <TableHead>Teacher</TableHead>
                        <TableHead>Assignment</TableHead>
                        <TableHead>Assignment Grade</TableHead>
                        <TableHead>Cycle Average</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {assignments.map((assignment) => {
                        const excusalStatus = getExcusalStatus(assignment.id);
                        
                        return (
                          <TableRow key={assignment.id} className="hover:bg-slate-50">
                            <TableCell className="font-medium">{assignment.course || '-'}</TableCell>
                            <TableCell>{assignment.section || '-'}</TableCell>
                            <TableCell>{assignment.course_short_description || '-'}</TableCell>
                            <TableCell>{assignment.teacher}</TableCell>
                            <TableCell className="text-sm">{assignment.assignment || '-'}</TableCell>
                            <TableCell>
                              <span className="font-semibold text-red-600">{assignment.assignment_grade || '-'}</span>
                            </TableCell>
                            <TableCell>
                              <span className="font-semibold text-red-600">{assignment.athletic_grade || '-'}</span>
                            </TableCell>
                            <TableCell>
                              {excusalStatus ? (
                                <Badge className={
                                  excusalStatus.status === 'pending' ? 'bg-orange-100 text-orange-800' :
                                  excusalStatus.status === 'approved' ? 'bg-green-100 text-green-800' :
                                  'bg-red-100 text-red-800'
                                }>
                                  {excusalStatus.status}
                                </Badge>
                              ) : (
                                <Badge className="bg-slate-100 text-slate-800">Active</Badge>
                              )}
                            </TableCell>
                            <TableCell>
                              {!excusalStatus ? (
                                <Button
                                  size="sm"
                                  onClick={() => handleRequestExcusal(assignment)}
                                  className="bg-red-700 hover:bg-red-800"
                                  disabled={blockedTime || hasASD}
                                  title={hasASD ? 'Students on the ASD roster cannot request excusals.' : undefined}
                                  data-testid={`request-excusal-${assignment.id}`}
                                >
                                  <FileText className="w-3 h-3 mr-1" />
                                  Request Excusal
                                </Button>
                              ) : (
                                <span className="text-xs text-slate-400">Requested</span>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>

            {excusals.length > 0 && (
              <Card className="border-none shadow-lg">
                <CardHeader>
                  <CardTitle>My Excusal Requests</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {excusals.map((excusal) => (
                      <div key={excusal.id} className="p-4 border border-slate-200 rounded-lg">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <p className="font-medium text-slate-900">{excusal.subject}</p>
                              <Badge className={
                                excusal.status === 'pending' ? 'bg-orange-100 text-orange-800' :
                                excusal.status === 'approved' ? 'bg-green-100 text-green-800' :
                                'bg-red-100 text-red-800'
                              }>
                                {excusal.status}
                              </Badge>
                            </div>
                            <p className="text-sm text-slate-600">
                              <strong>Teacher:</strong> {excusal.teacher_name}
                            </p>
                            <p className="text-sm text-slate-500 mt-1">
                              <strong>Your reason:</strong> {excusal.reason}
                            </p>
                            {excusal.proof_of_completion_url && (
                              <p className="text-xs text-blue-600 mt-1">
                                📎 <a href={excusal.proof_of_completion_url} target="_blank" rel="noopener noreferrer" className="underline">View uploaded proof</a>
                              </p>
                            )}
                            {excusal.teacher_notes && (
                              <p className="text-sm text-slate-600 mt-2 bg-slate-50 p-2 rounded">
                                <strong>Teacher response:</strong> {excusal.teacher_notes}
                              </p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </>
        )}

        {showExcusalForm && selectedAssignment && (
          <SubmitExcusalForm
            assignment={selectedAssignment}
            studentEmail={studentEmail}
            onClose={() => {
              setShowExcusalForm(false);
              setSelectedAssignment(null);
            }}
            onSuccess={() => {
              setShowExcusalForm(false);
              setSelectedAssignment(null);
              window.location.reload();
            }}
          />
        )}
      </div>
    </div>
  );
}